/**
 * StaleStatusDot — top-nav freshness indicator (IA §1.7 / §9.1).
 *
 * Small coloured dot sized for the header. Hover shows a tooltip with the
 * last-sync time and total trace count. Colour derivation lives in
 * `lib/stale.ts` so `<StaleStatusCard>` reuses the same thresholds.
 */

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ageToColor, ageToHuman, COLOR_CLASS } from '@/lib/stale';
import { cn } from '@/lib/utils';

export interface StaleStatusDotProps {
  lastSyncedAt: Date | null;
  ageMs: number | null;
  totalTraces?: number;
  loading?: boolean;
}

export function StaleStatusDot({
  ageMs,
  totalTraces,
  loading = false,
}: StaleStatusDotProps): React.ReactElement {
  // Loading → grey dot ("unknown"). We deliberately reuse the "grey" colour
  // so the UI doesn't show a false-healthy green until data lands.
  const color = loading ? 'grey' : ageToColor(ageMs);
  const humanAge = loading ? 'loading…' : ageToHuman(ageMs);
  // Bug P4 — user asked to drop the "N traces total" chrome at top-right.
  // `totalTraces` is accepted but intentionally unused in rendered text so
  // existing callers stay compatible.
  void totalTraces;
  const tooltipText = `Last synced ${humanAge}`;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-label={tooltipText}
            data-testid="stale-status-dot"
            data-color={color}
            className={cn(
              'inline-block h-2.5 w-2.5 rounded-full',
              COLOR_CLASS[color],
            )}
          />
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
